// The exam, walked through: a desktop, full-page guided tour of the real test.
// Left is the section explainer (what it is, what to watch for, the facts); right
// is a faithful mockup of THIS PORTAL'S actual exam screen for that task type,
// populated with a REAL seeded question (data/examScreens.js), with an arrow drawn
// from the explainer to the element that matters. Skill tabs, a question-range
// spine, prev/next with dots.
//
// The device screen mirrors the portal's own runner UI (blue-900 chrome, real
// passages, real A/B/C/D options), it is not a generic clone of another exam.
import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Play, Volume2 } from 'lucide-react';

import DiagramCard from '@/components/DiagramCard';
import ContentImage from '@/components/ContentImage';

import { EXAM_WALKTHROUGH, SKILL_ORDER, SKILL_LABEL } from '@/data/examWalkthrough';
import { EXAM_SCREENS } from '@/data/examScreens';
import { getExamWalkthrough, getSkillLabels } from '@/lib/examWalkthroughI18n';
import { useApp } from '@/lib/store';
import { langDir } from '@/lib/lang';

const bigRange = (r) => {
  if (/^Q/.test(r)) { const x = r.replace(/^Q/, '').replace(/\s*-\s*/g, '–'); return (x.includes('–') ? 'Questions ' : 'Question ') + x; }
  return r;
};

const AudioBar = () => (
  <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
    <div className="mb-1 text-[7.5px] uppercase tracking-wider text-slate-400">Recording · plays once</div>
    <div className="flex items-center gap-2">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-900">
        <Play className="h-2.5 w-2.5 fill-white text-white" />
      </span>
      <span className="text-[8px] tabular-nums text-slate-400">0:03</span>
      <div className="relative h-[3px] flex-1 rounded-full bg-slate-200">
        <div className="absolute inset-y-0 left-0 w-[38%] rounded-full bg-blue-900" />
        <span className="absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border border-white bg-blue-900 shadow-sm" style={{ left: '38%' }} />
      </div>
      <span className="text-[8px] tabular-nums text-slate-400">0:08</span>
      <Volume2 className="h-2.5 w-2.5 text-slate-400" />
    </div>
  </div>
);


// Portal-style A/B/C/D options, with the correct one highlighted exactly as the
// real CELPIP/PTE runner highlights a selection (blue-900).
function Options({ options, answerIndex, passRef }) {
  return (
    <div className="space-y-1">
      {(options || []).map((opt, oi) => {
        const correct = oi === answerIndex;
        return (
          <div key={oi} ref={correct ? passRef : undefined}
            className={`flex items-start gap-2 rounded-sm border px-2.5 py-1.5 text-[9.5px] leading-snug ${correct ? 'border-blue-900 bg-blue-50 text-slate-900' : 'border-transparent text-slate-700'}`}>
            <span className={`mt-px flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border text-[8px] font-semibold ${correct ? 'border-blue-900 bg-blue-900 text-white' : 'border-slate-300 text-slate-400'}`}>
              {String.fromCharCode(65 + oi)}
            </span>
            <span>{opt}</span>
          </div>
        );
      })}
    </div>
  );
}

function ExamScreen({ exam, step, passRef }) {
  const sc = (EXAM_SCREENS[exam] && EXAM_SCREENS[exam][step.tt]) || {};
  const mode = sc.mode || 'reading';

  const Header = (
    <div className="flex items-center justify-between bg-blue-900 px-4 py-2 text-white">
      <span className="text-[9px] font-semibold uppercase tracking-wider text-blue-200">{step.skillTag} · {step.qn}</span>
      <span className="rounded bg-blue-950/60 px-2 py-0.5 text-[11px] font-semibold tabular-nums">{step.timer}</span>
    </div>
  );
  const Bottom = (
    <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2">
      <span className="text-[9.5px] font-semibold text-slate-400">‹ Back</span>
      <span className="rounded-sm bg-blue-900 px-4 py-1.5 text-[9.5px] font-semibold text-white">NEXT</span>
    </div>
  );

  let body = null;
  if (mode === 'reading' || mode === 'diagram') {
    body = (
      <div className="grid h-full min-h-0 grid-cols-2 gap-2.5 p-3">
        <div className="flex min-h-0 flex-col overflow-hidden rounded border border-slate-300 bg-white">
          <div className="shrink-0 border-b border-slate-100 px-2.5 py-1 text-[8px] uppercase tracking-wider text-slate-400">{mode === 'diagram' ? 'Diagram' : 'Passage'}</div>
          <div className="min-h-0 flex-1 overflow-auto px-2.5 py-2 text-[9px] leading-snug text-slate-800">
            {mode === 'diagram'
              ? (sc.diagram ? <div style={{ zoom: 0.72 }}><DiagramCard diagram={sc.diagram} /></div> : null)
              : <p className="whitespace-pre-wrap">{sc.passage}</p>}
          </div>
        </div>
        <div className="flex min-h-0 flex-col overflow-hidden rounded border border-slate-300 bg-white">
          <div className="shrink-0 border-b border-slate-100 px-2.5 py-1.5 text-[9.5px] font-medium text-slate-900">{sc.prompt}</div>
          <div className="min-h-0 flex-1 overflow-auto px-2 py-2"><Options options={sc.options} answerIndex={sc.answerIndex} passRef={passRef} /></div>
        </div>
      </div>
    );
  } else if (mode === 'listening') {
    body = (
      <div className="mx-auto max-w-[88%] px-4 pt-3">
        <AudioBar />
        <div className="mt-2 text-[9.5px] font-medium text-slate-900">{sc.prompt}</div>
        <div className="mt-1.5"><Options options={sc.options} answerIndex={sc.answerIndex} passRef={passRef} /></div>
      </div>
    );
  } else if (mode === 'fib') {
    const typed = sc.typed ?? !(sc.options && sc.options.length);
    const ans = sc.firstAnswer || (sc.options || [])[sc.answerIndex] || '';
    const gaps = (sc.passage || '').split(/\{\{\d+\}\}|_{3,}|\[\.{2,}\]/);
    const filled = typed
      ? <span ref={passRef} className="mx-0.5 inline-flex items-center rounded-sm border-b-2 border-blue-900 bg-blue-50 px-1.5 align-middle text-[9px] font-semibold text-blue-900">{ans}</span>
      : <span ref={passRef} className="mx-0.5 inline-flex items-center gap-1 rounded border border-blue-900 bg-blue-50 px-1.5 align-middle text-[9px] font-semibold text-blue-900">{ans} ▾</span>;
    const empty = typed
      ? <span className="mx-0.5 inline-flex min-w-[34px] items-center justify-center border-b-2 border-slate-300 px-1.5 align-middle text-[9px] text-slate-300">&nbsp;</span>
      : <span className="mx-0.5 inline-flex min-w-[34px] items-center justify-center rounded border border-slate-300 bg-white px-1.5 align-middle text-[9px] text-slate-300">▾</span>;
    body = (
      <div className="mx-auto max-w-[90%] px-4 pt-3">
        {sc.audio && <div className="mb-2"><AudioBar /></div>}
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-[9.5px] leading-[2] text-slate-800">
          {gaps.map((g, gi) => (
            <React.Fragment key={gi}>
              {g}
              {gi < gaps.length - 1 && (gi === 0 ? filled : empty)}
            </React.Fragment>
          ))}
        </div>
        <p className="mt-2 text-[8px] text-slate-400">
          {typed
            ? 'Each blank is a box you type the word you hear into. The first is filled in to show the format.'
            : 'Each blank is its own dropdown. The first is filled in to show you the format.'}
        </p>
      </div>
    );
  } else if (mode === 'reorder') {
    body = (
      <div className="mx-auto max-w-[90%] space-y-1.5 px-4 pt-3">
        {(sc.paragraphs || []).map((p, i) => (
          <div key={i} ref={i === sc.firstIndex ? passRef : undefined}
            className={`flex gap-2 rounded border px-2.5 py-1.5 text-[9px] leading-snug ${i === sc.firstIndex ? 'border-blue-900 bg-blue-50' : 'border-slate-200 bg-white'}`}>
            <span className="font-bold text-slate-400">{i + 1}</span><span>{p}</span>
          </div>
        ))}
      </div>
    );
  } else if (mode === 'hiw') {
    body = (
      <div className="mx-auto max-w-[90%] px-4 pt-3">
        <AudioBar />
        <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-[10px] leading-[2]">
          {(sc.words || []).map((w, i) => (i === sc.wrongIndex
            ? <span key={i} ref={passRef} className="rounded bg-blue-100 px-1 font-semibold text-blue-900 underline decoration-blue-400">{w} </span>
            : <span key={i}>{w} </span>))}
        </div>
        <p className="mt-1.5 text-[8px] text-slate-400">Click the word that does not match the audio.</p>
      </div>
    );
  } else if (mode === 'dictation') {
    body = (
      <div className="mx-auto max-w-[88%] px-4 pt-3">
        <AudioBar />
        <div ref={passRef} className="mt-2.5 rounded border border-slate-200 bg-white px-3 py-4 text-[9px] text-slate-300">Type the sentence you hear…</div>
      </div>
    );
  } else if (mode === 'typed') {
    body = (
      <div className="mx-auto max-w-[90%] px-4 pt-3">
        {sc.audio && <div className="mb-2"><AudioBar /></div>}
        {sc.passage
          ? <div className="mb-2 max-h-[120px] overflow-hidden rounded border border-slate-200 bg-white px-2.5 py-2 text-[9px] leading-snug text-slate-700"><p className="whitespace-pre-wrap">{sc.passage}</p></div>
          : <div className="mb-2 rounded border border-slate-200 bg-white px-2.5 py-2 text-[9.5px] leading-snug text-slate-800">{sc.prompt}</div>}
        <div ref={passRef} className="flex h-[58px] items-end justify-between rounded border border-slate-200 bg-slate-50 px-2.5 py-2 text-[9px] text-slate-300">
          <span>Your response…</span><span className="text-slate-400">{sc.words || '0 words'}</span>
        </div>
      </div>
    );
  } else if (mode === 'read') {
    body = (
      <div className="mx-auto max-w-[86%] px-4 pt-3.5">
        <div ref={passRef} className="rounded border border-slate-200 bg-slate-50 px-3 py-3 text-[10px] leading-relaxed text-slate-800">{sc.passage}</div>
        <div className="mt-2.5 flex items-center gap-2 text-[9px] text-slate-500"><span className="h-2 w-2 rounded-full bg-red-500" /> Recording. Read the text aloud.</div>
      </div>
    );
  } else if (mode === 'speak') {
    body = (
      <div className="mx-auto max-w-[88%] px-4 pt-3.5">
        <div ref={passRef} className="rounded border border-slate-200 bg-white px-3 py-2.5 text-[9.5px] leading-snug text-slate-800">{sc.prompt}</div>
        <div className="mt-2.5 flex items-center gap-2 text-[9px] text-slate-500"><span className="h-2 w-2 rounded-full bg-red-500" /> Recording. Respond out loud.</div>
      </div>
    );
  } else if (mode === 'image') {
    body = (
      <div className="mx-auto max-w-[86%] px-4 pt-3.5">
        <div ref={passRef} className="overflow-auto rounded border border-slate-200 bg-slate-50 p-2">
          {sc.diagram
            ? <div style={{ zoom: 0.8 }}><DiagramCard diagram={sc.diagram} /></div>
            : <ContentImage imageRef={sc.imageRef} caption={sc.scene} compact />}
        </div>
        <div className="mt-2.5 flex items-center gap-2 text-[9px] text-slate-500"><span className="h-2 w-2 rounded-full bg-red-500" /> Recording. Describe what you see.</div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col bg-white text-slate-900">
      {Header}<div className="min-h-0 flex-1 overflow-y-auto">{body}</div>{Bottom}
    </div>
  );
}

// Reusable: render THIS portal's actual exam screen for a task type, populated with a
// real seeded question. Used in lessons so "what this part looks like" shows the real
// on-screen interface, not a list of bullets. Returns null if there's no screen for it.
// The exam screen is built at a fixed "natural" desktop size and then scaled to
// fit the frame, so it reads as a real full screen shrunk in proportion rather
// than a few chunky elements blown up to fill a small box.
const SCREEN_NAT_W = 960;
const SCREEN_NAT_H = Math.round((SCREEN_NAT_W * 10) / 16); // 16:10

export function ExamScreenCard({ exam, taskType }) {
  const ex = EXAM_WALKTHROUGH[exam] ? exam : 'celpip';
  const sc = EXAM_SCREENS[ex] && EXAM_SCREENS[ex][taskType];
  const ref = useRef(null);
  const fitRef = useRef(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const el = fitRef.current;
    if (!el) return undefined;
    const measure = () => setScale(el.clientWidth / SCREEN_NAT_W);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!sc) return null;
  let step = null;
  for (const sk of SKILL_ORDER) {
    const s = (EXAM_WALKTHROUGH[ex][sk] || []).find((st) => st.tt === taskType);
    if (s) { step = s; break; }
  }
  if (!step) step = { tt: taskType, skillTag: 'Reading', qn: 'Q1', timer: '', prog: 25 };
  return (
    <div className="relative bg-[#0b1220] rounded-[16px] p-3" style={{ boxShadow: '0 30px 60px -34px rgba(15,23,42,.4)' }} dir="ltr">
      <div ref={fitRef} className="relative overflow-hidden rounded-[8px] bg-white" style={{ aspectRatio: '16/10' }}>
        <div
          style={{
            position: 'absolute', top: 0, left: 0,
            width: SCREEN_NAT_W, height: SCREEN_NAT_H,
            transform: `scale(${scale})`, transformOrigin: 'top left',
          }}
        >
          <ExamScreen exam={ex} step={step} passRef={ref} />
        </div>
      </div>
    </div>
  );
}

export default function ExamWalkthrough({ exam: examProp }) {
  const params = useParams();
  const navigate = useNavigate();
  const { lang } = useApp();
  const exam = (examProp || params.exam || 'celpip').toLowerCase();
  // English is the structural source of truth; the overlay swaps translated
  // fields in-place so the renderer downstream never has to care about lang.
  const localized = getExamWalkthrough(lang);
  const skillLabels = getSkillLabels(lang);
  const ui = localized.ui;
  const dir = langDir(lang);
  const data = localized[exam] ? exam : 'celpip';

  const [skill, setSkill] = useState(SKILL_ORDER[0]);
  const [i, setI] = useState(0);
  const steps = localized[data][skill] || [];
  const step = steps[i] || steps[0];

  const stageRef = useRef(null), capRef = useRef(null), passRef = useRef(null);
  const [arrow, setArrow] = useState(null);

  const draw = useCallback(() => {
    const stage = stageRef.current, cap = capRef.current, tgt = passRef.current;
    if (!stage || !cap || !tgt) { setArrow(null); return; }
    const s = stage.getBoundingClientRect(), c = cap.getBoundingClientRect(), t = tgt.getBoundingClientRect();
    const labW = 168;
    // Under RTL the grid mirrors the columns (caption on the right, mockup on
    // the left) but getBoundingClientRect stays in physical viewport
    // coordinates. Mirror the LTR offsets so the arrow originates from the
    // *inner* edge of the caption and lands on the *inner* edge of the target
    // in either direction. The label sits to the outer side of the caption so
    // it never overlaps the mockup.
    const isRtl = dir === 'rtl';
    const ox = isRtl ? c.left - s.left - 22 : c.right - s.left + 22;
    const oy = c.top - s.top + c.height * 0.5;
    const ex = isRtl ? t.right - s.left + 6 : t.left - s.left - 6;
    const ey = t.top - s.top + t.height * 0.5;
    const cx = (ox + ex) / 2, cyc = Math.min(oy, ey) - 46, ang = Math.atan2(ey - cyc, ex - cx), ah = 9;
    setArrow({
      d: `M ${ox} ${oy} Q ${cx} ${cyc} ${ex} ${ey}`, org: [ox, oy],
      head: `${ex},${ey} ${ex - ah * Math.cos(ang - 0.5)},${ey - ah * Math.sin(ang - 0.5)} ${ex - ah * Math.cos(ang + 0.5)},${ey - ah * Math.sin(ang + 0.5)}`,
      labLeft: isRtl ? ox + 6 : ox - 6 - labW, labCY: oy, labW,
    });
  }, [dir]);

  useLayoutEffect(() => {
    const t1 = setTimeout(draw, 80);
    const t2 = setTimeout(draw, 260);
    window.addEventListener('resize', draw);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', draw); };
  }, [i, skill, draw]);

  useEffect(() => { setI(0); }, [skill]);
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowRight') setI((v) => Math.min(steps.length - 1, v + 1));
      if (e.key === 'ArrowLeft') setI((v) => Math.max(0, v - 1));
      if (e.key === 'Escape') navigate(`/${exam}/mocks`);
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length, exam]);

  const go = (k) => { if (k >= 0 && k < steps.length) setI(k); };
  const close = () => navigate(`/${exam}/mocks`);
  const meta = localized[data];

  const Tab = ({ on, onClick, children, testid }) => (
    <button onClick={onClick} data-testid={testid} className={`font-display font-semibold text-[12.5px] px-3.5 py-1.5 rounded-md transition ${on ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-slate-800'}`}>{children}</button>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="exam-walkthrough" data-exam={data} dir={dir}>
      <div className="lg:hidden flex flex-col items-center justify-center min-h-screen px-8 text-center">
        <div className="h-display text-2xl">{ui.mobile_title}</div>
        <p className="text-slate-500 mt-3 max-w-sm leading-relaxed">{ui.mobile_body}</p>
        <button onClick={close} data-testid="wt-mobile-back" className="mt-6 font-semibold text-indigo-600">{ui.btn_back.replace(/^[‹›]\s*|\s*[‹›]$/g, '').trim() || 'Back'}</button>
      </div>

      <div className="hidden lg:flex flex-col min-h-screen">
        <div className="border-b border-slate-200 bg-white/70 backdrop-blur">
          <div className="max-w-[1340px] mx-auto px-8 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-display text-[14px]">{meta.label}</span>
              <span className="text-slate-300">·</span>
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                {SKILL_ORDER.map((sk) => <Tab key={sk} on={skill === sk} onClick={() => setSkill(sk)} testid={`wt-skill-${sk}`}>{skillLabels[sk]}</Tab>)}
              </div>
            </div>
            <button onClick={close} data-testid="wt-close" className="font-display font-semibold text-[13px] text-slate-500 hover:text-slate-900">{ui.btn_close}</button>
          </div>
        </div>

        <div className="max-w-[1340px] mx-auto px-8 w-full flex-1">
          <header className="pt-6 pb-2">
            <div className="font-display font-semibold text-[11px] tracking-[0.22em] uppercase text-indigo-600">{ui.header_exam_walked_through} · {meta.label} · {skillLabels[skill]}</div>
            <div className="flex items-center flex-wrap mt-3.5 gap-y-2">
              {steps.map((s, k) => (
                <div key={s.range + k} className="flex items-center">
                  <button onClick={() => go(k)} title={s.cefr} data-testid={`wt-section-${k}`} className={`font-display font-semibold text-[12px] rounded-lg px-3 py-1.5 border transition ${k === i ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                    {s.range}
                  </button>
                  {k < steps.length - 1 && <div className="w-3.5 h-[2px] bg-slate-200" />}
                </div>
              ))}
            </div>
            {meta.spineNote && <div className="mt-2.5 text-[10.5px] text-slate-400 leading-snug max-w-[64ch]">{meta.spineNote}</div>}
          </header>

          <section ref={stageRef} className="relative grid items-center pb-8" style={{ gridTemplateColumns: '0.84fr 1.16fr', gap: 44, minHeight: 430 }}>
            <div ref={capRef} className="relative z-[5]">
              <div className="font-display font-semibold text-[10.5px] tracking-[0.2em] uppercase text-slate-400">{step.cefr}</div>
              <div className="font-display font-extrabold text-indigo-600 text-[16px] tracking-tight mt-2.5">{bigRange(step.range)}</div>
              <h2 className="h-display text-[25px] leading-tight mt-0.5">{step.title}</h2>
              <p className="text-slate-700 text-[15px] mt-3 leading-relaxed max-w-[42ch]">{step.lede}</p>
              <div className="mt-4 border-s-2 border-indigo-600 ps-4">
                <div className="font-display font-semibold text-[11px] tracking-[0.16em] uppercase text-indigo-600">{ui.label_what_to_watch_for}</div>
                <p className="text-slate-700 text-[13.5px] mt-1.5 leading-snug max-w-[40ch]">{step.watch}</p>
              </div>
              <div className="flex gap-5 mt-5">
                {step.facts.map(([n, t]) => <div key={t}><div className="h-display text-[20px]">{n}</div><div className="text-[11px] text-slate-500 mt-0.5">{t}</div></div>)}
              </div>
            </div>

            <div className="relative z-[2] flex justify-center items-center">
              <div className="w-full max-w-[660px]">
                <div className="relative bg-[#0b1220] rounded-[20px] p-[15px]" style={{ boxShadow: '0 60px 110px -40px rgba(15,23,42,.5), 0 34px 60px -34px rgba(15,23,42,.32)' }}>
                  <span className="absolute top-1.5 left-1/2 -translate-x-1/2 w-[5px] h-[5px] rounded-full bg-slate-700" />
                  <div className="relative bg-white rounded-[9px] overflow-hidden ewx-screen" style={{ aspectRatio: '16/10' }} key={data + skill + i} dir="ltr">
                    <ExamScreen exam={data} step={step} passRef={passRef} />
                  </div>
                </div>
              </div>
            </div>

            {arrow && (
              <svg className="absolute inset-0 pointer-events-none z-[8] overflow-visible" key={'a' + data + skill + i}>
                <circle className="ewx-arrow-dot" cx={arrow.org[0]} cy={arrow.org[1]} r={4} fill="#4f46e5" />
                <path className="ewx-arrow-path" d={arrow.d} fill="none" stroke="#4f46e5" strokeWidth={2.6} strokeLinecap="round" />
                <polygon className="ewx-arrow-head" points={arrow.head} fill="#4f46e5" />
              </svg>
            )}

            {arrow && step.arrow && (
              <div key={'lab' + data + skill + i} className="absolute z-[9] bg-white rounded-xl px-3 py-2.5 ewx-label"
                style={{ left: arrow.labLeft, top: arrow.labCY, width: arrow.labW, transform: 'translateY(-50%)', boxShadow: '0 16px 36px -16px rgba(15,23,42,.30), 0 4px 10px -6px rgba(15,23,42,.14)' }}>
                <div className="border-s-2 border-indigo-600 ps-2.5 font-display text-[11px] leading-snug text-slate-700 font-medium">{step.arrow}</div>
              </div>
            )}
          </section>
        </div>

        <div className="border-t border-slate-200 bg-slate-50/85 backdrop-blur">
          <div className="max-w-[1340px] mx-auto px-8 h-16 flex items-center justify-between">
            <button onClick={() => go(i - 1)} disabled={i === 0} data-testid="wt-back" className="font-display font-semibold text-[14px] px-5 py-2.5 rounded-[11px] border border-slate-200 bg-white disabled:opacity-40">{ui.btn_back}</button>
            <div className="flex items-center gap-4">
              <div className="flex gap-1.5">{steps.map((s, k) => <button key={s.range + k} onClick={() => go(k)} data-testid={`wt-dot-${k}`} className={`h-2 rounded-full transition-all ${k === i ? 'bg-indigo-600 w-5' : 'bg-slate-200 w-2'}`} />)}</div>
              <div className="font-display font-semibold text-[13px] text-slate-500">{meta.label} · {skillLabels[skill]} · <b className="text-slate-900">{i + 1}</b> / {steps.length}</div>
            </div>
            <button onClick={() => go(i + 1)} disabled={i === steps.length - 1} data-testid="wt-next" className="font-display font-semibold text-[14px] px-5 py-2.5 rounded-[11px] bg-slate-900 text-white border border-slate-900 disabled:opacity-40">{ui.btn_next}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
